'use client'

import { motion } from "framer-motion"

/**
 * Props for the `StepIndicator` component.
 */
interface StepIndicatorProps {
  /**
   * The index of the current active step (zero-based).
   */
  currentStep: number

  /**
   * The total number of steps in the process.
   */
  totalSteps: number
}

/**
 * `StepIndicator` is a visual component that displays progress through multiple steps.
 *
 * It renders a horizontal row of animated dots, with the active step highlighted using a larger width and stronger color.
 *
 * @example
 * ```tsx
 * <StepIndicator currentStep={1} totalSteps={4} />
 * ```
 *
 * @param props - {@link StepIndicatorProps}
 * @returns A row of animated step indicators.
 */
export function StepIndicator({ currentStep, totalSteps }: StepIndicatorProps) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: totalSteps }).map((_, i) => (
        <motion.div
          key={i}
          data-testid="step"
          className="h-2 rounded-full bg-primary/20"
          initial={{ width: "1rem" }}
          animate={{
            width: i === currentStep ? "2rem" : "1rem",
            backgroundColor:
              i <= currentStep
                ? "hsl(var(--primary))"
                : "hsl(var(--primary) / 0.2)",
          }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      ))}
    </div>
  )
}
